import React, { useState } from "react";
import {
  EuiAccordion,
  EuiButton,
  EuiCallOut,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiLoadingSpinner,
  EuiPanel,
  EuiSelect,
  EuiSpacer,
  EuiText,
  EuiTextArea,
} from "@elastic/eui";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { useToast } from "src/notifications";
import { useAnketaI18n } from "src/internationalization";
import { OtoboTicket } from "src/questionnaires";

const i18n = "anketa.opsanio.appliance.flyout.otobo";

interface HistoryEntry {
  type: string;
  name: string;
  createdAt: string;
  createdBy: string;
}

const errorText = (resp: { error?: any; data?: any }): string =>
  resp?.error?.service?.message ?? resp?.error?.message ?? "";

/**
 * The ticket's audit trail (state changes, moves, owner changes ...), loaded
 * through the OTOBO MCP server the first time it is expanded.
 */
export const TicketAuditTrail = ({
  ticketId,
  formatDate,
}: {
  ticketId: number;
  formatDate: (d: string) => string;
}): JSX.Element => {
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const load = async () => {
    if (entries !== null || loading || typeof keycloak?.token !== "string") return;
    setLoading(true);
    setFailed(null);
    const resp = await Get<any>({
      path: `v2/${AnketaResourcesEnum.opsanio}/otobo/ticket/${ticketId}/history`,
      responseType: "json",
      token: keycloak.token,
    });
    setLoading(false);
    const data = (resp.data as any)?.data;
    if (!resp.success || !Array.isArray(data)) {
      setFailed(
        errorText(resp) ||
          translate(`${i18n}.audit.failed`, {
            defaultMessage: "The audit trail could not be loaded.",
          }),
      );
      return;
    }
    setEntries(data);
  };

  return (
    <EuiAccordion
      id={`otobo-audit-${ticketId}`}
      buttonContent={translate(`${i18n}.audit.title`, {
        defaultMessage: "Audit trail",
      })}
      onToggle={(open) => {
        if (open) void load();
      }}
      paddingSize="s"
    >
      {loading ? (
        <EuiLoadingSpinner size="m" />
      ) : failed ? (
        <EuiText size="xs" color="danger">
          {failed}
        </EuiText>
      ) : entries && entries.length === 0 ? (
        <EuiText size="xs" color="subdued">
          {translate(`${i18n}.audit.empty`, { defaultMessage: "No entries." })}
        </EuiText>
      ) : (
        <div style={{ maxHeight: 260, overflowY: "auto" }}>
          {(entries ?? []).map((entry, index) => (
            <EuiFlexGroup
              key={`${entry.createdAt}-${index}`}
              gutterSize="s"
              responsive={false}
              style={{ fontSize: 12, padding: "2px 0" }}
            >
              <EuiFlexItem grow={false} style={{ minWidth: 130 }}>
                <EuiText size="xs" color="subdued">
                  {formatDate(entry.createdAt)}
                </EuiText>
              </EuiFlexItem>
              <EuiFlexItem grow={false} style={{ minWidth: 120 }}>
                <EuiText size="xs">
                  <strong>{entry.type}</strong>
                </EuiText>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiText size="xs">{entry.name}</EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
          ))}
        </div>
      )}
    </EuiAccordion>
  );
};

/** States offered when replying; "" keeps the ticket's state. */
const REPLY_STATES = ["", "open", "pending reminder", "closed successful"];

/**
 * Reply to the ticket (appends an article through the OTOBO MCP server) and
 * optionally change its state. Rendered only for `opsanio/otobo · create`.
 */
export const TicketReplyForm = ({
  ticket,
  onUpdated,
}: {
  ticket: OtoboTicket;
  onUpdated: (ticket: OtoboTicket) => void;
}): JSX.Element => {
  const { Post } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const { addToast } = useToast();
  const [subject, setSubject] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    if (body.trim() === "" || typeof keycloak?.token !== "string") return;
    setSending(true);
    setError(null);
    const resp = await Post<any>({
      path: `v2/${AnketaResourcesEnum.opsanio}/otobo/ticket/${ticket.TicketID}/reply`,
      value: { body, subject: subject.trim() || undefined, state: state || undefined },
      responseType: "json",
      token: keycloak.token,
    });
    setSending(false);
    if (!resp.success) {
      setError(
        errorText(resp) ||
          translate(`${i18n}.reply.failed`, {
            defaultMessage: "The reply could not be sent.",
          }),
      );
      return;
    }
    // Clear the draft only once OTOBO has the article.
    setBody("");
    setSubject("");
    setState("");
    addToast({
      color: "success",
      title: translate(`${i18n}.reply.sent`, { defaultMessage: "Reply added to the ticket" }),
      text: `#${ticket.TicketNumber}`,
    });
    const updated = (resp.data as any)?.data;
    if (updated) onUpdated(updated as OtoboTicket);
  };

  return (
    <EuiPanel paddingSize="m" hasBorder hasShadow={false}>
      <EuiText size="xs" color="subdued">
        <strong>
          {translate(`${i18n}.reply.title`, { defaultMessage: "Reply" })}
        </strong>
      </EuiText>
      <EuiSpacer size="s" />
      <EuiFormRow
        fullWidth
        label={translate(`${i18n}.reply.subject`, { defaultMessage: "Subject" })}
      >
        <EuiFieldText
          fullWidth
          compressed
          value={subject}
          placeholder={`Re: ${ticket.Title}`}
          onChange={(e) => setSubject(e.target.value)}
          disabled={sending}
        />
      </EuiFormRow>
      <EuiFormRow
        fullWidth
        label={translate(`${i18n}.reply.body`, { defaultMessage: "Message" })}
      >
        <EuiTextArea
          fullWidth
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={sending}
        />
      </EuiFormRow>
      <EuiFlexGroup gutterSize="s" alignItems="flexEnd" responsive={false} wrap>
        <EuiFlexItem grow={false} style={{ minWidth: 200 }}>
          <EuiFormRow
            label={translate(`${i18n}.reply.state`, { defaultMessage: "Set state" })}
          >
            <EuiSelect
              compressed
              value={state}
              onChange={(e) => setState(e.target.value)}
              disabled={sending}
              options={REPLY_STATES.map((value) => ({
                value,
                text:
                  value === ""
                    ? translate(`${i18n}.reply.stateKeep`, {
                        defaultMessage: "Keep current state",
                      })
                    : value,
              }))}
            />
          </EuiFormRow>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton
            fill
            size="s"
            iconType="email"
            onClick={send}
            isLoading={sending}
            isDisabled={body.trim() === ""}
          >
            {translate(`${i18n}.reply.send`, { defaultMessage: "Send reply" })}
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>
      {error && (
        <>
          <EuiSpacer size="s" />
          <EuiCallOut size="s" color="danger" iconType="alert" title={error} />
        </>
      )}
    </EuiPanel>
  );
};

/** Stock OTOBO priorities. */
const PRIORITIES = ["1 very low", "2 low", "3 normal", "4 high", "5 very high"];

/**
 * Open a new ticket in the appliance's queue (via the OTOBO MCP server when
 * configured). Rendered only for `opsanio/otobo · create`.
 */
export const NewTicketForm = ({
  instanceName,
  queue,
  onCreated,
  onCancel,
}: {
  instanceName: string;
  queue: string;
  onCreated: (ticket: { TicketID: number; TicketNumber: string }) => void;
  onCancel: () => void;
}): JSX.Element => {
  const { Post } = useApi();
  const { keycloak, user } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const { addToast } = useToast();
  const [title, setTitle] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const [priority, setPriority] = useState<string>("3 normal");
  const [customerUser, setCustomerUser] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const canSend = title.trim() !== "" && body.trim() !== "" && !sending;

  const create = async () => {
    if (!canSend || typeof keycloak?.token !== "string") return;
    setSending(true);
    setError(null);
    const resp = await Post<any>({
      path: `v2/${AnketaResourcesEnum.opsanio}/otobo/instance/${encodeURIComponent(
        instanceName,
      )}/ticket`,
      value: {
        title: title.trim(),
        body,
        priority,
        customerUser: customerUser.trim() || undefined,
      },
      responseType: "json",
      token: keycloak.token,
    });
    setSending(false);
    const created = (resp.data as any)?.data;
    if (!resp.success || !created?.TicketID) {
      setError(
        errorText(resp) ||
          translate(`${i18n}.create.failed`, {
            defaultMessage: "The ticket could not be created.",
          }),
      );
      return;
    }
    addToast({
      color: "success",
      title: translate(`${i18n}.create.success`, { defaultMessage: "Ticket created" }),
      text: `#${created.TicketNumber || created.TicketID}`,
    });
    onCreated(created);
  };

  return (
    <EuiPanel paddingSize="m" hasBorder hasShadow={false}>
      <EuiText size="xs" color="subdued">
        <strong>
          {translate(`${i18n}.create.title`, { defaultMessage: "New ticket" })}
        </strong>{" "}
        · {queue}
      </EuiText>
      <EuiSpacer size="s" />
      <EuiFormRow
        fullWidth
        label={translate(`${i18n}.create.field.title`, { defaultMessage: "Title" })}
      >
        <EuiFieldText
          fullWidth
          compressed
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={sending}
          maxLength={250}
        />
      </EuiFormRow>
      <EuiFormRow
        fullWidth
        label={translate(`${i18n}.create.field.body`, { defaultMessage: "Message" })}
      >
        <EuiTextArea
          fullWidth
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={sending}
        />
      </EuiFormRow>
      <EuiFlexGroup gutterSize="s" responsive={false} wrap>
        <EuiFlexItem style={{ minWidth: 160 }}>
          <EuiFormRow
            label={translate(`${i18n}.create.field.priority`, { defaultMessage: "Priority" })}
          >
            <EuiSelect
              compressed
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              disabled={sending}
              options={PRIORITIES.map((p) => ({ value: p, text: p }))}
            />
          </EuiFormRow>
        </EuiFlexItem>
        <EuiFlexItem style={{ minWidth: 220 }}>
          <EuiFormRow
            label={translate(`${i18n}.create.field.customerUser`, {
              defaultMessage: "Customer user",
            })}
            helpText={translate(`${i18n}.create.field.customerUserHelp`, {
              defaultMessage: "OTOBO customer user login; empty = you",
            })}
          >
            <EuiFieldText
              compressed
              value={customerUser}
              placeholder={user?.email ?? ""}
              onChange={(e) => setCustomerUser(e.target.value)}
              disabled={sending}
            />
          </EuiFormRow>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size="s" />
      <EuiFlexGroup gutterSize="s" justifyContent="flexEnd" responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiButton size="s" onClick={onCancel} disabled={sending}>
            {translate("anketa.common.cancel", { defaultMessage: "Cancel" })}
          </EuiButton>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton
            fill
            size="s"
            iconType="plusInCircle"
            onClick={create}
            isLoading={sending}
            isDisabled={!canSend}
          >
            {translate(`${i18n}.create.submit`, { defaultMessage: "Create ticket" })}
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>
      {error && (
        <>
          <EuiSpacer size="s" />
          <EuiCallOut size="s" color="danger" iconType="alert" title={error} />
        </>
      )}
    </EuiPanel>
  );
};
